// Thành phần giao diện dùng chung cho các tab của trang Tổng quan tài liệu
import type { Dayjs } from 'dayjs';
import { ArrowDownRight, ArrowUpRight, Info, Minus } from 'lucide-react';
import { Button, Tooltip } from 'antd';
import type { Doc, DocCategory, DocType, Employee } from '../data';
import type { Period, PeriodKey } from '../stats';

export interface DashCtx {
  docs: Doc[];
  scoped: Doc[];
  allDocs: Doc[];
  people: Employee[];
  types: DocType[];
  categories: DocCategory[];
  p: Period;
  now: Dayjs;
  period: PeriodKey;
  setPeriod: (k: PeriodKey) => void;
  unit: string;
  goList: (q: Record<string, string>) => void;
}

export const COLORS = {
  brand: '#3e91ab', green: '#1D9E75', blue: '#378ADD', amber: '#EF9F27', red: '#E24B4A', gray: '#888780', violet: '#7F77DD', coral: '#D85A30',
};
export const rateColor = (r: number) => (r >= 0.7 ? COLORS.green : r >= 0.5 ? COLORS.amber : COLORS.red);
export const pct = (x: number) => `${Math.round(x * 100)}%`;
export const fmtDays = (d: number) => (d < 1 ? `${Math.max(1, Math.round(d * 24))} giờ` : `${d.toFixed(1).replace('.', ',')} ngày`);

type DeltaKind = 'count' | 'percent' | 'points' | 'days';

export function Delta({ cur, prev, kind, goodWhenUp = true, suffix }: { cur: number; prev: number; kind: DeltaKind; goodWhenUp?: boolean; suffix?: string }) {
  const diff = cur - prev;
  if (Math.abs(diff) < 1e-9) return <span className="flex items-center gap-1 text-xs text-slate-400"><Minus size={13} /> Không đổi {suffix}</span>;
  const up = diff > 0;
  const good = up === goodWhenUp;
  const text = kind === 'count' ? `${up ? '+' : '−'}${Math.abs(diff)}`
    : kind === 'percent' ? (prev ? `${up ? '+' : '−'}${Math.abs(Math.round((diff / prev) * 100))}%` : 'mới')
    : kind === 'points' ? `${up ? '+' : '−'}${Math.abs(Math.round(diff * 100))} điểm`
    : `${up ? 'chậm hơn' : 'nhanh hơn'} ${Math.abs(diff).toFixed(1).replace('.', ',')} ngày`;
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${good ? 'text-emerald-600' : 'text-red-500'}`}>
      {kind !== 'days' && (up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />)}{text} <span className="font-normal text-slate-400">{suffix}</span>
    </span>
  );
}

export const Card = ({ title, hint, info, children, className = '', extra }: {
  title: React.ReactNode; hint?: React.ReactNode; info?: string; children: React.ReactNode; className?: string; extra?: React.ReactNode;
}) => (
  <section className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}>
    <div className="mb-3 flex items-center justify-between gap-2">
      <h2 className="flex items-center gap-1.5 text-[15px] font-semibold text-slate-800">
        {title}
        {info && <Tooltip title={info}><Info size={14} className="text-slate-400" /></Tooltip>}
      </h2>
      {extra ?? (hint && <span className="text-right text-xs text-slate-400">{hint}</span>)}
    </div>
    {children}
  </section>
);

/** Thẻ chỉ số nhỏ dùng trong các tab phân tích */
export const Stat = ({ label, value, sub, tone = 'text-slate-800', info, onClick }: {
  label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: string; info?: string; onClick?: () => void;
}) => {
  const body = (
    <>
      <div className="flex items-center gap-1 text-[13px] text-slate-500">{label}{info && <Tooltip title={info}><Info size={13} className="text-slate-400" /></Tooltip>}</div>
      <div className={`mt-1 text-[24px] font-bold leading-tight ${tone}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </>
  );
  return onClick
    ? <button onClick={onClick} className="rounded-xl border border-slate-200 bg-white p-4 text-left transition-shadow hover:border-[#3e91ab]/40 hover:shadow-sm">{body}</button>
    : <div className="rounded-xl border border-slate-200 bg-white p-4">{body}</div>;
};

export const StatGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">{children}</div>
);

/** Thanh tỷ lệ ngang */
export const RateBar = ({ value, color, height = 8 }: { value: number; color: string; height?: number }) => (
  <span className="block overflow-hidden rounded-full bg-slate-100" style={{ height }}>
    <span className="block h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, value * 100))}%`, background: color }} />
  </span>
);

/** Gợi ý khi kỳ đang chọn quá ít dữ liệu để phân tích */
export const ThinDataHint = ({ count, min = 5, ctx, what }: { count: number; min?: number; ctx: DashCtx; what: string }) =>
  count >= min || ctx.period === '12m' ? null : (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
      <Info size={16} />
      <span className="flex-1">Kỳ đang chọn chỉ có <b>{count}</b> {what} — số liệu dưới đây có thể chưa đủ để kết luận.</span>
      <Button size="small" onClick={() => ctx.setPeriod('12m')}>Xem 12 tháng</Button>
    </div>
  );

export const Legend = ({ items }: { items: { label: string; color: string; dashed?: boolean; line?: boolean }[] }) => (
  <div className="mb-2 flex flex-wrap gap-4 text-xs text-slate-500">
    {items.map(i => (
      <span key={i.label} className="flex items-center gap-1.5">
        {i.line || i.dashed
          ? <span className="w-4 border-t-2" style={{ borderColor: i.color, borderStyle: i.dashed ? 'dashed' : 'solid' }} />
          : <span className="h-2.5 w-2.5 rounded-sm" style={{ background: i.color }} />}
        {i.label}
      </span>
    ))}
  </div>
);
